import type { Disposable } from '@patsnap/tahiti-core';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { RavenService, type RavenService as RavenServiceType } from '@telecom/shared/services/raven-service/raven-service';
import { I18nService, type I18nService as I18nServiceType } from '@telecom/shared/services/i18n';
import type { Config, Driver } from 'driver.js';
import { TRACK_EVENT_TYPE } from '@/constants';
import { TRACK_EVENT_AGENT_REPORT_ACTIONS_SPOTLIGHTED } from '@/constants/track-events';
import {
    OnboardingEligibilityService,
    type OnboardingEligibilityService as OnboardingEligibilityServiceType,
} from '../../common/services/onboarding-eligibility-service';
import {
    ACTION_GUIDE_OVERLAY_COLOR,
    ACTION_GUIDE_OVERLAY_OPACITY,
} from '../../common/constants';
import { hasSeenOnboarding, markOnboardingSeen } from '../../common/utils/onboarding-storage';
import { createReportActionGuideStep } from '../../common/utils/report-action-guide';
import { registerOnboardingLocaleMessages } from '../../common/utils/onboarding-locale';
import { messages } from '../../common/locales';
import {
    ReportActionGuideBubblePresenter,
    type ReportActionGuideBubblePresenter as ReportActionGuideBubblePresenterType,
} from '../../common/services/report-action-guide-bubble-presenter';
import {
    DesignFtoOnboardingRuntime,
    type DesignFtoOnboardingRuntime as DesignFtoOnboardingRuntimeType,
} from '../design-fto-onboarding-runtime';
import type { DesignFtoReportOnboardingService } from '../design-fto-report-onboarding-service';

/** Design FTO 报告 onboarding 存储 key 的版本化前缀。 */
const STORAGE_PREFIX = 'design-fto-report-onboarding-v1';
/** 报告内容的稳定选择器。 */
const REPORT_CONTENT_SELECTOR = '[data-testid="design-fto-report-content"]';
/** 报告分享入口的稳定选择器。 */
const REPORT_SHARE_SELECTOR = '[data-testid="design-fto-report-share"]';
/** 报告导出入口的稳定选择器。 */
const REPORT_EXPORT_SELECTOR = '[data-testid="design-fto-report-export"]';
/** Agent Header 分享区域的层叠上下文。 */
const REPORT_HEADER_LAYER_SELECTOR = '[data-testid="design-fto-report-header-layer"]';
/** 报告详情面板层叠上下文的稳定选择器。 */
const REPORT_DETAIL_LAYER_SELECTOR = '[data-testid="design-fto-report-detail-layer"]';
/** Teleport Header 层叠上下文的稳定选择器。 */
const REPORT_ACTION_LAYER_SELECTOR = '[data-testid="design-fto-report-action-layer"]';
/** 引导期间临时解除祖先层叠上下文的样式类。 */
const RELEASED_LAYER_CLASS = 'design-fto-report-onboarding-layer-released';
/** 分享按钮作为 Driver 之外辅助高亮的样式类。 */
const SECONDARY_HIGHLIGHT_CLASS = 'design-fto-report-action-secondary-highlight';
/** 达到阅读阈值后，展示报告操作引导前要求的停留时长。 */
const ACTION_GUIDE_DELAY_MS = 800;
/** 等待报告组件和导出按钮渲染的最长时间。 */
const TARGET_WAIT_TIMEOUT_MS = 5000;
/** 开始报告操作引导所需的阅读进度。 */
const READING_PROGRESS_THRESHOLD = 0.2;

type DebugStageInput = 'actions' | 'export';

/** 当前 Design FTO 报告 onboarding 使用的身份和任务上下文。 */
interface OnboardingContext {
    /** 当前实时生成成功的任务 ID，仅用于生命周期去重。 */
    taskId: string;
    /** 当前浏览器用户的稳定身份。 */
    identityId: string;
}

/**
 * Design FTO 报告分享/导出引导编排器。
 *
 * 页面只转交真实报告成功事件，service 负责用户级一次性状态、异步目标等待、
 * 双目标静态 Driver 遮罩、点击归因和全部临时资源清理。
 */
@injectable()
export class DesignFtoReportOnboardingServiceImpl implements DesignFtoReportOnboardingService {
    /** 当前报告上下文。 */
    private _context?: OnboardingContext;
    /** 报告滚动内容。 */
    private _reportContentElement?: Element;
    /** 分享按钮。 */
    private _reportShareElement?: Element;
    /** 导出按钮。 */
    private _reportExportElement?: Element;
    /** Agent Header 层叠上下文。 */
    private _reportHeaderLayerElement?: Element;
    /** 报告详情面板层叠上下文。 */
    private _reportDetailLayerElement?: Element;
    /** Teleport Header 层叠上下文。 */
    private _reportActionLayerElement?: Element;
    /** 当前报告操作引导 Driver。 */
    private _driver?: Driver;
    /** 分享按钮说明气泡的清理句柄。 */
    private _shareGuideBubbleDisposable?: Disposable;
    /** 达到阅读阈值后的操作引导停留检测定时器。 */
    private _actionGuideTimerId?: number;
    /** 当前身份是否已展示报告操作引导。 */
    private _hasShownActionGuide = false;
    /** 当前报告操作引导是否可归因到下一次按钮点击。 */
    private _isActionGuideAttributionArmed = false;
    /** 当前报告是否已主动操作，阻止后续补展示引导。 */
    private _isActionGuideSuppressed = false;
    /** 分享目标异步等待期间防止重复启动。 */
    private _isActionGuideStarting = false;
    /** 报告交互监听是否已绑定。 */
    private _areReportListenersBound = false;
    /** service 是否处于有效页面生命周期。 */
    private _isInitialized = false;
    /** 当前正在异步启动的任务 ID。 */
    private _startingTaskId?: string;
    /** 当前生命周期已经处理过的任务 ID。 */
    private _handledTaskId?: string;
    /** 本地调试是否需要跳过阅读阶段直接展示操作引导。 */
    private _debugShowActionsImmediately = false;
    /** 使销毁前异步结果失效的生命周期序号。 */
    private _lifecycleId = 0;

    // eslint-disable-next-line max-params
    constructor(
        /** 提供 DOM、Driver、身份、存储和定时器能力。 */
        @inject(DesignFtoOnboardingRuntime)
        private readonly _runtime: DesignFtoOnboardingRuntimeType,
        /** 上报报告分享和导出引导展示埋点。 */
        @inject(RavenService)
        private readonly _ravenService: RavenServiceType,
        /** 限制报告引导仅面向海外个人用户。 */
        @inject(OnboardingEligibilityService)
        private readonly _onboardingEligibilityService: OnboardingEligibilityServiceType,
        /** 提供报告操作说明的当前语言文案。 */
        @inject(I18nService)
        private readonly _i18nService: I18nServiceType,
        /** 为分享辅助目标展示独立说明气泡。 */
        @inject(ReportActionGuideBubblePresenter)
        private readonly _reportActionGuideBubblePresenter: ReportActionGuideBubblePresenterType,
    ) {}

    /** 初始化报告引导 service；重复调用不会改变生命周期。 */
    initialize(): void {
        if (this._isInitialized || !this._onboardingEligibilityService.isEligible()) return;
        this._initializeWithoutEligibilityCheck();
    }

    /** 接收真实报告成功事件，并异步启动尚未展示的阶段。 */
    notifyReportGenerated(taskId: string): void {
        if (!this._isInitialized || !taskId) return;
        if (this._startingTaskId === taskId || this._handledTaskId === taskId) return;

        const lifecycleId = this._lifecycleId;
        this._startingTaskId = taskId;
        void this._start(taskId, lifecycleId)
            .then((started) => {
                if (this._isCurrent(lifecycleId) && started) this._handledTaskId = taskId;
            })
            .catch((error) => {
                if (this._isCurrent(lifecycleId)) {
                    this._clearRuntimeState();
                    this._clearReportContext();
                }
                this._runtime.warn('Failed to start Design FTO report onboarding:', error);
            })
            .finally(() => {
                if (this._startingTaskId === taskId) this._startingTaskId = undefined;
            });
    }

    /** 消费一次操作引导归因，并阻止后续再次补展示。 */
    consumeActionGuide(): boolean {
        if (!this._context) return false;

        const isGuided = this._isActionGuideAttributionArmed;
        this._isActionGuideSuppressed = true;
        this._isActionGuideAttributionArmed = false;
        this._clearActionGuide();
        return isGuided;
    }

    /** 仅在 Vite 开发环境绕过受众判断和指定阶段的存储状态。 */
    debugStart(taskId: string, stage: DebugStageInput = 'actions'): void {
        if (!import.meta.env.DEV || !taskId) return;
        if (!this._isInitialized) this._initializeWithoutEligibilityCheck();
        this._debugShowActionsImmediately = stage === 'actions' || stage === 'export';
        this.notifyReportGenerated(taskId);
    }

    /** 清理当前页面全部可见引导和异步资源。 */
    destroy(): void {
        this._isInitialized = false;
        this._lifecycleId += 1;
        this._startingTaskId = undefined;
        this._handledTaskId = undefined;
        this._debugShowActionsImmediately = false;
        this._clearRuntimeState();
        this._clearReportContext();
    }

    /** 绕过受众判断初始化，供正常入口和仅开发调试入口共用。 */
    private _initializeWithoutEligibilityCheck(): void {
        this._isInitialized = true;
    }

    /** 解析身份、异步目标和一次性状态后启动当前阶段。 */
    // eslint-disable-next-line complexity
    private async _start(taskId: string, lifecycleId: number): Promise<boolean> {
        const identityId = await this._runtime.getIdentityId();
        if (!this._isCurrent(lifecycleId) || !identityId) return false;

        await this._runtime.waitForRender();
        if (!this._isCurrent(lifecycleId)) return false;

        const [reportContentElement, reportExportElement] = await Promise.all([
            this._runtime.waitForElement(REPORT_CONTENT_SELECTOR, TARGET_WAIT_TIMEOUT_MS),
            this._runtime.waitForElement(REPORT_EXPORT_SELECTOR, TARGET_WAIT_TIMEOUT_MS),
        ]);
        if (!this._isCurrent(lifecycleId) || !reportContentElement || !reportExportElement) return false;

        this._clearRuntimeState();
        this._context = { taskId, identityId };
        this._reportContentElement = reportContentElement;
        this._reportExportElement = reportExportElement;
        this._reportDetailLayerElement = this._runtime.querySelector(REPORT_DETAIL_LAYER_SELECTOR) ?? undefined;
        this._reportActionLayerElement = this._runtime.querySelector(REPORT_ACTION_LAYER_SELECTOR) ?? undefined;
        this._isActionGuideSuppressed = false;
        this._hasShownActionGuide = this._debugShowActionsImmediately
            ? false
            : hasSeenOnboarding(this._runtime, this._getStorageKey(identityId));
        const shouldShowActionGuideImmediately = this._debugShowActionsImmediately;
        this._debugShowActionsImmediately = false;

        if (this._hasShownActionGuide) return true;

        this._bindReportListeners();
        if (shouldShowActionGuideImmediately) {
            void this._showActionGuide();
        }
        return true;
    }

    /** 阅读进度达到 20% 且停留 800ms 后展示报告操作引导。 */
    private _scheduleActionGuide(): void {
        if (
            this._hasShownActionGuide
            || this._isActionGuideSuppressed
            || !this._reportContentElement
        ) return;

        const progress = this._runtime.getReadingProgress(this._reportContentElement);
        if (progress < READING_PROGRESS_THRESHOLD) {
            this._clearActionGuideTimer();
            return;
        }

        this._clearActionGuideTimer();
        const lifecycleId = this._lifecycleId;
        this._actionGuideTimerId = this._runtime.setTimeout(() => {
            this._actionGuideTimerId = undefined;
            if (!this._isCurrent(lifecycleId) || !this._reportContentElement) return;
            if (this._runtime.getReadingProgress(this._reportContentElement) < READING_PROGRESS_THRESHOLD) return;
            void this._showActionGuide();
        }, ACTION_GUIDE_DELAY_MS);
    }

    /** 创建 Driver 遮罩，以导出为主目标并静态提升分享按钮。 */
    // eslint-disable-next-line complexity, max-lines-per-function
    private async _showActionGuide(): Promise<void> {
        const context = this._context;
        const exportElement = this._reportExportElement;
        if (this._isActionGuideStarting) return;
        if (
            !context
            || !exportElement
            || this._hasShownActionGuide
            || this._isActionGuideSuppressed
        ) {
            this._unbindReportListeners();
            return;
        }

        const lifecycleId = this._lifecycleId;
        this._isActionGuideStarting = true;
        try {
            const shareElement = this._reportShareElement
                ?? await this._runtime.waitForElement(REPORT_SHARE_SELECTOR, TARGET_WAIT_TIMEOUT_MS);
            if (
                !this._isCurrent(lifecycleId)
                || this._context !== context
                || !shareElement
                || this._isActionGuideSuppressed
            ) return;
            if (
                !this._runtime.isElementEnabled(shareElement)
                || !this._runtime.isElementEnabled(exportElement)
            ) return;

            this._reportShareElement = shareElement;
            this._reportHeaderLayerElement = this._runtime.querySelector(REPORT_HEADER_LAYER_SELECTOR) ?? undefined;
            this._releaseStackingLayers();
            const driverInstance = this._runtime.createDriver(this._createDriverConfig());
            this._driver = driverInstance;
            this._runtime.addClass(shareElement, SECONDARY_HIGHLIGHT_CLASS);
            registerOnboardingLocaleMessages(this._i18nService, messages);
            driverInstance.highlight(createReportActionGuideStep(
                exportElement,
                this._i18nService.t('onboarding.reportActions.exportDescription'),
            ));
            if (!driverInstance.isActive()) {
                this._clearActionGuide(false);
                return;
            }
            this._shareGuideBubbleDisposable = this._reportActionGuideBubblePresenter.show({
                target: shareElement,
                description: this._i18nService.t('onboarding.reportActions.shareDescription'),
            });

            this._isActionGuideAttributionArmed = true;
            this._hasShownActionGuide = true;
            markOnboardingSeen(this._runtime, this._getStorageKey(context.identityId));
            this._runtime.addDocumentEventListener('click', this._handleDocumentInteraction);
            this._runtime.addDocumentEventListener('keydown', this._handleDocumentInteraction);
            this._track(TRACK_EVENT_AGENT_REPORT_ACTIONS_SPOTLIGHTED);
        } catch (error) {
            this._clearActionGuide();
            this._runtime.warn('Failed to show Design FTO report action guide:', error);
        } finally {
            if (this._context === context) {
                this._isActionGuideStarting = false;
                if (!this._driver?.isActive()) {
                    this._unbindReportListeners();
                }
            }
        }
    }

    /** 创建以导出按钮为主目标的 Driver 配置。 */
    private _createDriverConfig(): Config {
        return {
            animate: true,
            overlayColor: ACTION_GUIDE_OVERLAY_COLOR,
            overlayOpacity: ACTION_GUIDE_OVERLAY_OPACITY,
            allowClose: true,
            allowScroll: true,
            overlayClickBehavior: () => this._clearActionGuide(),
            disableActiveInteraction: false,
            allowKeyboardControl: true,
            stagePadding: 4,
            stageRadius: 10,
            onDestroyed: (_element, _step, { driver: destroyedDriver }) => {
                if (this._driver !== destroyedDriver) return;
                this._clearActionGuide(false);
            },
        };
    }

    /** 报告滚动会在阅读进度达标后安排操作引导。 */
    private readonly _handleReportScroll: EventListener = () => {
        if (this._driver?.isActive()) {
            this._driver.refresh();
            return;
        }
        this._scheduleActionGuide();
    };

    /** 报告点击会关闭当前激活的操作引导。 */
    private readonly _handleReportClick: EventListener = () => {
        if (this._driver?.isActive()) this._clearActionGuide();
    };

    /** 页面其他点击或键盘操作会结束当前操作引导。 */
    // eslint-disable-next-line complexity
    private readonly _handleDocumentInteraction: EventListener = (event) => {
        if (
            event instanceof KeyboardEvent
            && (event.key === 'Enter' || event.key === ' ')
            && this._isActionTarget(event.target)
        ) return;
        if (event.type === 'click' && this._isActionTarget(event.target)) return;
        this._clearActionGuide();
    };

    /** 判断交互来源是否为当前分享或导出按钮。 */
    private _isActionTarget(target: EventTarget | null): boolean {
        if (!(target instanceof Node)) return false;
        return Boolean(
            this._reportShareElement?.contains(target)
            || this._reportExportElement?.contains(target),
        );
    }

    /** 绑定报告自身的滚动和点击监听。 */
    private _bindReportListeners(): void {
        if (this._areReportListenersBound || !this._reportContentElement) return;
        this._runtime.addEventListener(this._reportContentElement, 'scroll', this._handleReportScroll);
        this._runtime.addEventListener(this._reportContentElement, 'click', this._handleReportClick);
        this._areReportListenersBound = true;
    }

    /** 移除报告自身的滚动和点击监听。 */
    private _unbindReportListeners(): void {
        if (!this._areReportListenersBound || !this._reportContentElement) return;
        this._runtime.removeEventListener(this._reportContentElement, 'scroll', this._handleReportScroll);
        this._runtime.removeEventListener(this._reportContentElement, 'click', this._handleReportClick);
        this._areReportListenersBound = false;
    }

    /** 临时解除 Agent Header、报告详情和 Teleport Header 的层叠限制。 */
    private _releaseStackingLayers(): void {
        if (this._reportHeaderLayerElement) {
            this._runtime.addClass(this._reportHeaderLayerElement, RELEASED_LAYER_CLASS);
        }
        if (this._reportDetailLayerElement) {
            this._runtime.addClass(this._reportDetailLayerElement, RELEASED_LAYER_CLASS);
        }
        if (this._reportActionLayerElement) {
            this._runtime.addClass(this._reportActionLayerElement, RELEASED_LAYER_CLASS);
        }
    }

    /** 恢复 Agent Header、报告详情和 Teleport Header 原有层叠上下文。 */
    private _restoreStackingLayers(): void {
        if (this._reportHeaderLayerElement) {
            this._runtime.removeClass(this._reportHeaderLayerElement, RELEASED_LAYER_CLASS);
        }
        if (this._reportDetailLayerElement) {
            this._runtime.removeClass(this._reportDetailLayerElement, RELEASED_LAYER_CLASS);
        }
        if (this._reportActionLayerElement) {
            this._runtime.removeClass(this._reportActionLayerElement, RELEASED_LAYER_CLASS);
        }
    }

    /** 清理操作 Driver、辅助高亮、监听器和临时层叠样式。 */
    private _clearActionGuide(destroyDriver = true): void {
        this._isActionGuideAttributionArmed = false;
        this._clearActionGuideTimer();
        this._runtime.removeDocumentEventListener('click', this._handleDocumentInteraction);
        this._runtime.removeDocumentEventListener('keydown', this._handleDocumentInteraction);
        this._shareGuideBubbleDisposable?.dispose();
        this._shareGuideBubbleDisposable = undefined;
        if (this._reportShareElement) {
            this._runtime.removeClass(this._reportShareElement, SECONDARY_HIGHLIGHT_CLASS);
        }
        this._restoreStackingLayers();

        const driverInstance = this._driver;
        this._driver = undefined;
        if (destroyDriver && driverInstance?.isActive()) driverInstance.destroy();
        this._unbindReportListeners();
    }

    /** 清理当前页面全部可见 UI、监听器和定时器。 */
    private _clearRuntimeState(): void {
        this._isActionGuideStarting = false;
        this._clearActionGuide();
        this._unbindReportListeners();
    }

    /** 清空当前任务目标和阶段状态。 */
    private _clearReportContext(): void {
        this._context = undefined;
        this._reportContentElement = undefined;
        this._reportShareElement = undefined;
        this._reportExportElement = undefined;
        this._reportHeaderLayerElement = undefined;
        this._reportDetailLayerElement = undefined;
        this._reportActionLayerElement = undefined;
        this._hasShownActionGuide = false;
        this._isActionGuideSuppressed = false;
        this._isActionGuideStarting = false;
    }

    /** 清理报告操作引导延迟计时器。 */
    private _clearActionGuideTimer(): void {
        if (this._actionGuideTimerId === undefined) return;
        this._runtime.clearTimeout(this._actionGuideTimerId);
        this._actionGuideTimerId = undefined;
    }

    /** 判断异步回调是否仍属于当前有效生命周期。 */
    private _isCurrent(lifecycleId: number): boolean {
        return this._isInitialized && this._lifecycleId === lifecycleId;
    }

    /** 生成按用户隔离的报告操作引导一次性展示 key。 */
    private _getStorageKey(identityId: string): string {
        return `${STORAGE_PREFIX}:report-export:${encodeURIComponent(identityId)}`;
    }

    /** 上报 Design FTO onboarding 埋点，仅附带当前事件中会变化的明细。 */
    private _track(eventCode: string, detail?: Record<string, string>): void {
        void this._ravenService.track(eventCode, {
            eventType: TRACK_EVENT_TYPE.DesignFTO,
            sourceType: eventCode,
            ...(detail ? { eventDetailStr: detail } : {}),
        }).catch(() => undefined);
    }
}
